import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { TREND_RANGES, type DashboardKpis, type DashboardTrends, type TrendRangeId } from './types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/** The API caches the figures for 60 seconds; asking more often than that only returns the same copy. */
export const DASHBOARD_REFRESH_MS = 60_000;

/** A refusal (403) is an answer, not an accident: it is not retried. One retry for anything else. */
const retryOnce = (failureCount: number, error: ApiError): boolean =>
  error.status !== 403 && error.status !== 401 && failureCount < 1;

/**
 * `GET /api/v1/admin/dashboard/kpis` - superadmin only (lot K2). Re-read every minute while the tab is visible (react-query pauses
 * the interval in a background tab); every read is audited by the server as `dashboard.kpi`, which is why nothing here polls faster.
 */
export function useKpisQuery(): UseQueryResult<DashboardKpis, ApiError> {
  const { accessToken } = useSession();
  return useQuery({
    queryKey: ['dashboard', 'kpis'],
    queryFn: ({ signal }) =>
      apiRequest<DashboardKpis>('/admin/dashboard/kpis', { accessToken, signal }),
    enabled: accessToken !== null,
    refetchInterval: DASHBOARD_REFRESH_MS,
    staleTime: DASHBOARD_REFRESH_MS / 2,
    retry: retryOnce,
  });
}

/** `GET /api/v1/admin/dashboard/trends?bucket=&periods=` - sales, orders and new merchants per day or month. */
export function useTrendsQuery(range: TrendRangeId): UseQueryResult<DashboardTrends, ApiError> {
  const { accessToken } = useSession();
  const { bucket, periods } = TREND_RANGES[range];
  return useQuery({
    queryKey: ['dashboard', 'trends', bucket, periods],
    queryFn: ({ signal }) =>
      apiRequest<DashboardTrends>(
        `/admin/dashboard/trends?${new URLSearchParams({ bucket, periods: String(periods) }).toString()}`,
        {
          accessToken,
          signal,
        },
      ),
    enabled: accessToken !== null,
    refetchInterval: DASHBOARD_REFRESH_MS,
    staleTime: DASHBOARD_REFRESH_MS / 2,
    retry: retryOnce,
  });
}
